import React, { useState } from 'react';
import { X, User, Key, Eye, EyeOff, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ResetPasswordModal({ isOpen, onClose }: ResetPasswordModalProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  type ApiError = { response?: { data?: { message?: string } } };
  type LookupUsernameResponse = { username?: string | null };

  const normalizeActivationCode = (value: string) =>
    value.replace(/[–—−]/g, '-').toUpperCase();

  const lookupUsernameByActivationCode = async (code: string): Promise<string | null> => {
    const trimmed = normalizeActivationCode(code).trim();
    if (!trimmed) return null;
    try {
      const res = await api.post<LookupUsernameResponse>('/auth/lookup-username', {
        activationCode: trimmed,
      });
      const found = res.data?.username;
      return typeof found === 'string' && found.trim() ? found.trim() : null;
    } catch {
      return null;
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError(t('resetPassword.error.minLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.error.mismatch'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        username: username.trim(),
        activationCode: normalizeActivationCode(activationCode).trim(),
        newPassword
      });
      setSuccess(t('resetPassword.success'));
      setTimeout(() => {
        onClose();
        // Reset form
        setUsername('');
        setActivationCode('');
        setNewPassword('');
        setConfirmPassword('');
        setSuccess('');
      }, 2000);
    } catch (err: unknown) {
      const enteredUsername = username.trim();
      const foundUsername = await lookupUsernameByActivationCode(activationCode);
      if (foundUsername && foundUsername !== enteredUsername) {
        // Just inform the user, do not auto-fill
        setError(t('resetPassword.error.foundUsername', { username: foundUsername }));
        return;
      }

      const backendMessage = (err as ApiError)?.response?.data?.message;
      setError(backendMessage || t('resetPassword.error.failed'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="relative bg-white/90 backdrop-blur-xl rounded-[1.75rem] shadow-2xl shadow-sky-200/40 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-white/70">
        <div className="px-6 py-5 bg-gradient-to-r from-sky-300/80 via-pink-200/80 to-amber-200/80 text-slate-800 flex items-center justify-between">
          <h3 className="text-lg font-extrabold flex items-center gap-2">
            <span className="text-2xl">🧩</span> {t('resetPassword.title')}
          </h3>
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-2xl flex items-center justify-center bg-white/60 hover:bg-white/80 text-slate-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-slate-600 mb-6 text-sm font-semibold">{t('resetPassword.subtitle')}</p>
          
          <form onSubmit={handleReset} className="space-y-5">
            {/* Username */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700">{t('resetPassword.username.label')}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-2xl bg-white/90 focus:ring-4 focus:ring-sky-100 focus:border-sky-300 outline-none transition-all text-sm"
                  placeholder={t('resetPassword.username.placeholder')}
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Activation Code */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700">{t('resetPassword.code.label')}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type="text"
                  value={activationCode}
                  onChange={(e) => setActivationCode(normalizeActivationCode(e.target.value))}
                  className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-2xl bg-white/90 focus:ring-4 focus:ring-amber-100 focus:border-amber-300 outline-none transition-all font-mono text-sm"
                  placeholder={t('resetPassword.code.placeholder')}
                  required
                />
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                💡 {t('resetPassword.code.hint')}
              </p>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700">{t('resetPassword.newPassword.label')}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="block w-full pl-10 pr-12 py-3 border border-slate-200 rounded-2xl bg-white/90 focus:ring-4 focus:ring-sky-100 focus:border-sky-300 outline-none transition-all text-sm"
                  placeholder={t('resetPassword.newPassword.placeholder')}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center h-10 w-10 justify-center rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                💡 {t('resetPassword.newPassword.hint')}
              </p>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700">{t('resetPassword.confirmPassword.label')}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-2xl bg-white/90 focus:ring-4 focus:ring-sky-100 focus:border-sky-300 outline-none transition-all text-sm"
                  placeholder={t('resetPassword.confirmPassword.placeholder')}
                  required
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Messages */}
            {error && (
              <div className="bg-rose-50 text-rose-600 text-sm p-3 rounded-2xl border border-rose-100 flex items-center gap-2">
                <span>🚫</span> {error}
              </div>
            )}
            
            {success && (
              <div className="bg-emerald-50 text-emerald-700 text-sm p-3 rounded-2xl border border-emerald-100 flex items-center gap-2">
                <span>✅</span> {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-2xl shadow-xl shadow-pink-200/60 text-sm font-extrabold text-white bg-gradient-to-r from-[#ff4da6] via-[#ff7a59] to-[#ffd166] hover:brightness-105 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-pink-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.99]"
            >
              {loading ? t('resetPassword.processing') : t('resetPassword.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
